/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
/* eslint-disable no-underscore-dangle, object-shorthand */

import React, { useEffect, useState, useRef } from "react";
import { validateAll } from "indicative/validator";
import FloatingLabelInput from "../../../components/elements/Inputs/FloatingLabelInput";
import DrawerPanel from "../../../components/elements/DrawerPanel/DrawerPanel";
import { DbAllTerminals } from "../../../types/api.types";
import { DrawerButton } from "../../../components/elements/Button";
import { handleValidationError } from "../../../utils/formHandlers";
import Loading from "../../../components/elements/Loading/Loading";
import { ErrorToast, SuccessToast } from "../../../components/blocks/Alert";
import { getAllCustomerApi } from "../../../redux/slice/customers/customers.api";
import { getCommunitiesApi } from "../../../redux/slice/communities/communities.api";
import { ReAssignTerminalApi } from "../../../redux/slice/posterminals/posterminals.api";

const getDesc = (isAssigned: boolean) => {
    if (isAssigned) return "Reassign terminal to new communities/entities";
    return "First time assignment of terminals requires terminals to be assigned to communities before reassigning to individuals";
};

type Props = {
    handleClose(): void;
    row: DbAllTerminals;
};

const fields = [
    { name: "accountReference", label: "Account Reference (entity reference)" },
] as const;

const ReassignTerminals = ({ handleClose, row }: Props) => {
    const { isAssigned } = row;

    const [filteredCommunities, setFilteredCommunities] = useState<any[]>([]);
    const [communities, setCommunities] = useState([]);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const [formData, setFormData] = useState({
        initialAccountReference: "",
        accountReference: "",
        communityReference: "",
        communityReferenceLabel: "",
    });

    const communityOptions = communities.map((c: any) => ({
        _id: c._id,
        label: c.displayCommunityName,
        value: c.communityReference,
        location: c.communityLocation,
        category: c.communityCategory,
        members: c.membersCount,
        owner: `${c?.communityOwnerUserRecord?.first_name || ""} ${
            c?.communityOwnerUserRecord?.last_name || ""
        }`,
    }));

    const fetchCommunities = async () => {
        setLoading(true);
        try {
            const res = await getCommunitiesApi(2000, 1);
            setLoading(false);
            setCommunities(res.communities);
            return res;
        } catch (error) {
            setLoading(false);
            return null;
        }
    };

    const fetchCustomers = async (query?: any) => {
        try {
            setLoading(true);
            const res = await getAllCustomerApi(2000, 1, query);
            return res;
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    };

    const handleTerminalReassignment = async () => {
        setLoading(true);
        try {
            const query = {
                userPhoneNumber: formData.accountReference,
            };
            const userRes = await fetchCustomers(query);
            const accountReference = userRes?.customers?.[0].wallet_key;

            if (!accountReference) {
                throw new Error("Invalid account reference. No user found.");
            }

            // const payload = {
            //     accountReference: accountReference,
            //     initialAccountReference: formData.initialAccountReference,
            //     terminalId: row.terminalId,
            //     alias: row.terminalId,
            //     serialNumber: row.serialNumber,
            //     assign: true,
            //     type: "full",
            // }

            // console.log("Payload: ", payload)

            await ReAssignTerminalApi({
                accountReference: accountReference,
                initialAccountReference: formData.initialAccountReference,
                terminalId: row.terminalId,
                alias: row.terminalId,
                serialNumber: row.serialNumber,
                assign: true,
                type: "full",
            });

            SuccessToast("Terminal Re-Assigned Successfully");
        } catch (error) {
            ErrorToast(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e: React.SyntheticEvent) => {
        e.preventDefault();

        const rules = {
            accountReference: "required",
            communityReference: "required",
            initialAccountReference: "required",
        };

        const message = {
            "accountReference.required": "Account reference cannot be blank",
            "initialAccountReference.required":
                "Initial account reference cannot be blank",
            "communityReference.required": "Please select a community",
        };

        validateAll(formData, rules, message)
            .then(() => {
                setErrors({});
                handleTerminalReassignment();
            })
            .catch(err => {
                const formattedErr = handleValidationError(err);
                setErrors(formattedErr);
            });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, name } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        if (!row) return;
        fetchCommunities();
    }, [row]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setFilteredCommunities([]);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <DrawerPanel
            title={isAssigned ? "Re-assign terminal" : "Assign Terminal"}
            description={getDesc(isAssigned)}
            open
            handleClose={handleClose}
        >
            <form onSubmit={handleSubmit}>
                {fields.map(({ label, name }) => (
                    <FloatingLabelInput
                        key={name}
                        name={name}
                        label={label}
                        value={formData[name]}
                        onChange={handleChange}
                        error={errors[name]}
                    />
                ))}

                <div className="mb-4 relative" ref={dropdownRef}>
                    <label
                        htmlFor="communityReference"
                        className="block mb-2 text-sm font-medium"
                    >
                        Select Community
                    </label>
                    <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring focus:border-blue-500"
                        placeholder="Search community"
                        value={formData.communityReferenceLabel || ""}
                        onChange={e => {
                            const keyword = e.target.value.toLowerCase();
                            setFormData(prev => ({
                                ...prev,
                                communityReferenceLabel: e.target.value,
                            }));
                            if (keyword === "") {
                                setFilteredCommunities([]);
                                return;
                            }
                            const filtered = communityOptions.filter(c =>
                                [c.label, c.location, c.category, c.owner]
                                    .join(" ")
                                    .toLowerCase()
                                    .includes(keyword)
                            );
                            setFilteredCommunities(filtered);
                        }}
                        onFocus={() => {
                            setFilteredCommunities(communityOptions);
                        }}
                    />

                    {filteredCommunities.length > 0 && (
                        <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {filteredCommunities.map(item => (
                                <li
                                    key={item.value}
                                    role="button"
                                    tabIndex={0}
                                    className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                                    onClick={() => {
                                        setFormData(prev => ({
                                            ...prev,
                                            communityReference: item.value,
                                            communityReferenceLabel: item.label,
                                            initialAccountReference: item._id, // ✅ use _id as initial account reference
                                        }));
                                        setFilteredCommunities([]);
                                    }}
                                >
                                    <div className="font-medium">
                                        {item.label}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        {item.location && (
                                            <span>{item.location}</span>
                                        )}
                                        {item.category && (
                                            <span> · {item.category}</span>
                                        )}
                                        {item.owner && (
                                            <span> · Owner: {item.owner}</span>
                                        )}
                                        {typeof item.members === "number" && (
                                            <span>
                                                {" "}
                                                · {item.members} members
                                            </span>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                    {errors.communityReference && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.communityReference}
                        </p>
                    )}
                </div>

                <DrawerButton type="submit" disabled={loading}>
                    {loading ? <Loading size="xs" /> : "Submit"}
                </DrawerButton>
            </form>
        </DrawerPanel>
    );
};

export default ReassignTerminals;
